import { useCallback, useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, badge } from '../../utils/uiStyles';
import BpoImportSection from './BpoImportSection';
import CustomerCenterSection from './CustomerCenterSection';
import CustomerLinkSection from './CustomerLinkSection';
import RefTablesSection from './RefTablesSection';
import WeightsSection from './WeightsSection';

/* 센터 실적 대시보드 «기준 데이터» — 소구역 [실적 센터]·[월 목표]·[고객사 마스터]·[미연결 고객사명]·[고객사 실적 센터]·
 * [날짜별 가중치]·[BPO 가져오기](can_manage 만).
 * · 권한은 서버(/center-perf/ref/centers)가 정한다 — can_manage = 순서·소계 그룹·수기값·기준일·BPO 가져오기,
 *   can_edit_access = 권한 센터·사용 여부·별칭·실적 센터 추가·미배정 다시 연결·보고. 화면은 그 값대로 편집 칸만 바꿔 그린다.
 * · 행마다 «저장»은 **바뀐 칸만** PATCH 로 보낸다. 서버는 권한 밖 칸이 하나라도 있으면 403, 값이 틀리면 400 이고
 *   일부만 저장하지 않는다. 응답은 전체 목록이라 그대로 다시 그린다(다른 행에서 고치던 값은 남긴다).
 * · 월 목표·고객사 마스터는 BPO 가져오기로 들어온 값을 보기만 한다(조회 범위는 서버가 정한다).
 * · 미연결 고객사명 = 업로드 원문 → 고객사 마스터 표준명 연결. 실적 센터 선택지는 이 화면의 실적 센터 목록, 연결·해제는 can_manage 만.
 * · 고객사 실적 센터 = 파일 실적 센터의 고객사 출고를 같은 권한 센터의 다른 실적 센터에 저장하는 예외. 추가·삭제는 can_manage 만.
 * · 날짜별 가중치 = 월 목표 진척도 계수 달력. 보기는 기준 데이터 열람 범위 전부, 저장·비우기·요일 기본값은 can_manage 만(서버가 다시 판정).
 */
const MANUAL = [
  ['max_perf', '최대실적'],
  ['std_capa_day', '표준 CAPA(일)'],
  ['storage_capa_plt', '보관 CAPA(PLT)'],
  ['current_storage_plt', '현보관량(PLT)'],
];
const MANAGE_KEYS = ['sort_order', 'subtotal_group', ...MANUAL.map(([k]) => k), 'manual_as_of'];
const ACCESS_KEYS = ['access_token', 'enabled'];
// [키, 이름, can_manage 만]
const SECTIONS = [['centers', '실적 센터'], ['targets', '월 목표'], ['customers', '고객사 마스터'], ['links', '미연결 고객사명'],
  ['splits', '고객사 실적 센터'], ['weights', '날짜별 가중치'], ['bpo', 'BPO 가져오기', true]];

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap', verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right' };
const inp = { padding: '4px 6px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' };
const chip = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 4px 2px 8px', border: `1px solid ${COLORS.borderSoft}`, borderRadius: '999px', fontSize: '12px', marginRight: '4px', marginBottom: '3px' };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};
const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
};

// 서버 값 → 입력 칸 값(문자열·불리언). 바뀐 칸 판정은 이 모양끼리 비교한다.
const draftOf = (c) => ({
  sort_order: c.sort_order === null || c.sort_order === undefined ? '' : String(c.sort_order),
  subtotal_group: c.subtotal_group || '',
  ...Object.fromEntries(MANUAL.map(([k]) => [k, c[k] === null || c[k] === undefined ? '' : String(c[k])])),
  manual_as_of: c.manual_as_of || '',
  access_token: c.access_token || '',
  enabled: !!c.enabled,
});

// 바뀐 칸만 PATCH 본문으로 — 권한 밖 칸은 처음부터 넣지 않는다. 숫자 판정은 서버 몫이라 문자열 그대로 보낸다(빈칸 = null).
export function patchOf(center, draft, { canManage, canEditAccess }) {
  const base = draftOf(center);
  const keys = [...(canManage ? MANAGE_KEYS : []), ...(canEditAccess ? ACCESS_KEYS : [])];
  const out = {};
  keys.forEach((k) => {
    if (!draft || draft[k] === base[k]) return;
    const v = draft[k];
    if (k === 'enabled') out[k] = !!v;
    else if (k === 'sort_order') {
      const t = String(v).trim();
      out[k] = /^\d+$/.test(t) ? Number(t) : t;
    } else if (k === 'subtotal_group' || k === 'access_token') out[k] = String(v).trim();
    else {
      const t = String(v).trim();
      out[k] = t === '' ? null : t;
    }
  });
  return out;
}

function AccessCell({ c, draft, canEditAccess, orgCenters, onChange }) {
  const token = canEditAccess ? draft.access_token : (c.access_token || '');
  const known = orgCenters.some((o) => o.token === token);
  const stale = !!c.access_token && !c.access_token_valid;
  if (!canEditAccess) {
    return (
      <td data-col="access_token" style={td}>
        {c.access_token ? (c.access_center_name || c.access_token) : <span style={{ color: COLORS.textMuted }}>없음(관리자·운영·AX실만)</span>}
        {stale && <span style={{ ...badge('warn'), marginLeft: '6px' }}>조직도에 없음</span>}
      </td>
    );
  }
  return (
    <td data-col="access_token" style={td}>
      <select aria-label={`${c.name} 권한 센터`} value={token} style={inp} onChange={(e) => onChange(e.target.value)}>
        <option value="">없음(관리자·운영·AX실만)</option>
        {orgCenters.map((o) => <option key={o.token} value={o.token}>{o.name}</option>)}
        {token && !known && <option value={token}>{token} (조직도에 없음)</option>}
      </select>
      {stale && <span style={{ ...badge('warn'), marginLeft: '6px' }}>조직도에 없음</span>}
    </td>
  );
}

export default function RefDataTab() {
  const [section, setSection] = useState('centers');
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [aliasInputs, setAliasInputs] = useState({});
  const [newName, setNewName] = useState('');
  const [msg, setMsg] = useState(null);             // { tone: 'ok' | 'err', text }
  const [busy, setBusy] = useState(false);

  // 응답(전체 목록)을 그린다. reset 행(또는 'all')만 입력 칸을 서버 값으로 되돌리고, 다른 행에서 고치던 값은 남긴다.
  const apply = useCallback((payload, reset = 'all') => {
    setData(payload);
    setDrafts((prev) => {
      const next = {};
      (payload?.centers || []).forEach((c) => {
        next[c.id] = reset === 'all' || reset === c.id || !prev[c.id] ? draftOf(c) : prev[c.id];
      });
      return next;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    centerPerfAPI.refCenters()
      .then((r) => { if (alive) apply(r.data || { centers: [] }); })
      .catch((e) => { if (alive) setLoadError(detailOf(e, '기준 데이터를 불러오지 못했습니다.')); });
    return () => { alive = false; };
  }, [apply]);

  if (loadError) return <div role="alert" style={{ ...card, color: COLORS.dangerText, fontSize: '13px' }}>{loadError}</div>;
  if (!data) return <div style={{ ...card, color: COLORS.textMuted, fontSize: '13px' }}>기준 데이터를 불러오는 중…</div>;

  const canManage = !!data.can_manage;
  const canEditAccess = !!data.can_edit_access;
  const editable = canManage || canEditAccess;
  const centers = data.centers || [];
  const orgCenters = data.org_centers || [];
  const report = data.report || null;

  const run = async (fn, okText, reset) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      const payload = r.data || { centers: [] };
      apply(payload, reset);
      setMsg({ tone: 'ok', text: typeof okText === 'function' ? okText(payload) : okText });
      return true;
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '저장하지 못했습니다.') });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (id, key, value) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const save = (c) => {
    const body = patchOf(c, drafts[c.id], { canManage, canEditAccess });
    if (!Object.keys(body).length) return;
    run(() => centerPerfAPI.updateCenter(c.id, body), `'${c.name}' 을(를) 저장했습니다`, c.id);
  };

  const addAlias = async (c) => {
    const alias = (aliasInputs[c.id] || '').trim();
    if (!alias) return;
    const ok = await run(() => centerPerfAPI.addAlias(c.id, alias), (d) => {
      const n = d.relinked?.assigned || 0;
      return `'${c.name}' 에 별칭 '${alias}' 을(를) 더했습니다${n ? ` — 미배정 업로드 ${n}건을 다시 연결했습니다` : ''}`;
    }, null);
    if (ok) setAliasInputs((p) => ({ ...p, [c.id]: '' }));
  };

  const removeAlias = async (c, a) => {
    const ok = await confirmDialog(`'${c.name}' 의 별칭 '${a.alias}' 을(를) 지웁니다. 이미 이어진 저장 값은 그대로 둡니다.`, { danger: true });
    if (!ok) return;
    run(() => centerPerfAPI.removeAlias(c.id, a.id), `'${c.name}' 의 별칭 '${a.alias}' 을(를) 지웠습니다`, null);
  };

  const addCenter = async () => {
    const name = newName.trim();
    if (!name) return;
    const ok = await run(() => centerPerfAPI.createCenter({ name }), `실적 센터 '${name}' 을(를) 추가했습니다`, null);
    if (ok) setNewName('');
  };

  const relink = () => run(() => centerPerfAPI.relink(), (d) => {
    const r = d.relinked || {};
    return `미배정 업로드 ${r.assigned || 0}건을 다시 연결했습니다${r.conflicts ? ` · 같은 날짜 값이 이미 있어 ${r.conflicts}건은 그대로 둡니다` : ''}`;
  }, null);

  const headers = ['순서', '실적 센터', '소계 그룹', '권한 센터', '별칭', '사용', ...MANUAL.map(([, l]) => l), '기준일', '저장 행 수'];
  if (editable) headers.push('');

  return (
    <div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        {SECTIONS.filter(([, , manageOnly]) => !manageOnly || canManage).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={section === k} onClick={() => setSection(k)}
            style={btn(section === k ? COLORS.primarySoft : COLORS.bgHover || COLORS.borderSoft, section === k ? COLORS.primary : COLORS.text)}>
            {label}
          </button>
        ))}
      </div>

      {section === 'centers' && (
        <div style={card}>
          <div style={hint}>
            실적 센터 = 업로드·조회·교체 단위(BPO 이름)입니다. 파일 실적요약 B2 가 고른 실적 센터의 권한 센터와 다르면 업로드 때
            확인 경고가 뜹니다(별칭은 1단계 옛 업로드를 실적 센터에 잇는 데 씁니다).
            {editable ? '' : ' 이 화면은 읽기 전용입니다.'}
            {canManage && !canEditAccess ? ' 순서·소계 그룹·수기값만 고칠 수 있습니다(권한 센터·사용·별칭은 운영 관리자·시스템 관리자).' : ''}
          </div>
          {msg && (
            <div role={msg.tone === 'err' ? 'alert' : 'status'}
              style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: msg.tone === 'err' ? COLORS.dangerText : COLORS.successText }}>
              {msg.text}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="perf-centers" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>{headers.map((h, i) => <th key={h || `act-${i}`} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {centers.length === 0 && (
                  <tr><td colSpan={headers.length} style={{ ...td, color: COLORS.textMuted }}>실적 센터가 없습니다.</td></tr>
                )}
                {centers.map((c) => {
                  const draft = drafts[c.id] || draftOf(c);
                  const dirty = Object.keys(patchOf(c, draft, { canManage, canEditAccess })).length > 0;
                  const counts = c.counts || {};
                  return (
                    <tr key={c.id} data-center={c.name} style={{ opacity: c.enabled ? 1 : 0.6 }}>
                      <td data-col="sort_order" style={td}>
                        {canManage
                          ? <input aria-label={`${c.name} 순서`} type="number" min="0" value={draft.sort_order} style={{ ...inp, width: '60px' }}
                              onChange={(e) => setDraft(c.id, 'sort_order', e.target.value)} />
                          : c.sort_order}
                      </td>
                      <td data-col="name" style={{ ...td, fontWeight: 600 }}>{c.name}</td>
                      <td data-col="subtotal_group" style={td}>
                        {canManage
                          ? <input aria-label={`${c.name} 소계 그룹`} value={draft.subtotal_group} maxLength={50} style={{ ...inp, width: '90px' }}
                              onChange={(e) => setDraft(c.id, 'subtotal_group', e.target.value)} />
                          : (c.subtotal_group || '—')}
                      </td>
                      <AccessCell c={c} draft={draft} canEditAccess={canEditAccess} orgCenters={orgCenters}
                        onChange={(v) => setDraft(c.id, 'access_token', v)} />
                      <td data-col="aliases" style={{ ...td, whiteSpace: 'normal', minWidth: '160px' }}>
                        {(c.aliases || []).map((a) => (
                          <span key={a.id} data-alias={a.alias} style={chip}>
                            <span>{a.alias}</span>
                            {canEditAccess && a.alias !== c.name && (
                              <button type="button" aria-label={`${c.name} 별칭 ${a.alias} 삭제`} disabled={busy}
                                onClick={() => removeAlias(c, a)}
                                style={{ ...btn(COLORS.dangerSoft, COLORS.dangerText), padding: '0 6px' }}>×</button>
                            )}
                          </span>
                        ))}
                        {canEditAccess && (
                          <span style={{ display: 'inline-flex', gap: '4px' }}>
                            <input aria-label={`${c.name} 새 별칭`} value={aliasInputs[c.id] || ''} maxLength={200} placeholder="새 별칭"
                              style={{ ...inp, width: '110px' }}
                              onChange={(e) => setAliasInputs((p) => ({ ...p, [c.id]: e.target.value }))} />
                            <button type="button" aria-label={`${c.name} 별칭 추가`} disabled={busy || !(aliasInputs[c.id] || '').trim()}
                              onClick={() => addAlias(c)} style={btn(COLORS.primary)}>+</button>
                          </span>
                        )}
                      </td>
                      <td data-col="enabled" style={td}>
                        {canEditAccess
                          ? <input type="checkbox" aria-label={`${c.name} 사용`} checked={!!draft.enabled}
                              onChange={(e) => setDraft(c.id, 'enabled', e.target.checked)} />
                          : (c.enabled ? '사용' : '안 함')}
                      </td>
                      {MANUAL.map(([k, label]) => (
                        <td key={k} data-col={k} style={tdNum}>
                          {canManage
                            ? <input aria-label={`${c.name} ${label}`} inputMode="decimal" value={draft[k]} style={{ ...inp, width: '90px', textAlign: 'right' }}
                                onChange={(e) => setDraft(c.id, k, e.target.value)} />
                            : fmtNum(c[k])}
                        </td>
                      ))}
                      <td data-col="manual_as_of" style={td}>
                        {canManage
                          ? <input aria-label={`${c.name} 기준일`} type="date" value={draft.manual_as_of} style={inp}
                              onChange={(e) => setDraft(c.id, 'manual_as_of', e.target.value)} />
                          : (c.manual_as_of || '—')}
                      </td>
                      <td data-col="counts" style={{ ...td, color: COLORS.textSub }}>
                        업로드 {counts.uploads || 0} · 출고 {counts.shipments || 0} · 근무 {counts.labor || 0}
                      </td>
                      {editable && (
                        <td style={td}>
                          <button type="button" aria-label={`${c.name} 저장`} disabled={busy || !dirty} onClick={() => save(c)}
                            style={{ ...btn(COLORS.primary), opacity: busy || !dirty ? 0.5 : 1 }}>저장</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canEditAccess && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
              <input aria-label="새 실적 센터 이름" value={newName} maxLength={50} placeholder="새 실적 센터 이름" style={{ ...inp, width: '180px', fontSize: '13px' }}
                onChange={(e) => setNewName(e.target.value)} />
              <button type="button" disabled={busy || !newName.trim()} onClick={addCenter} style={btn(COLORS.primary)}>실적 센터 추가</button>
              <button type="button" disabled={busy} onClick={relink} style={btn(COLORS.surface, COLORS.text)}>미배정 다시 연결</button>
            </div>
          )}
          {canEditAccess && report && <ReportPanel report={report} />}
        </div>
      )}
      {section === 'targets' && <RefTablesSection kind="targets" />}
      {section === 'customers' && <RefTablesSection kind="customers" />}
      {section === 'links' && <CustomerLinkSection centers={centers.map((c) => c.name)} canManage={canManage} />}
      {section === 'splits' && (
        <CustomerCenterSection centers={centers.map((c) => ({ name: c.name, access_token: c.access_token }))} canManage={canManage} />
      )}
      {section === 'weights' && <WeightsSection canManage={canManage} />}
      {section === 'bpo' && canManage && <BpoImportSection />}
    </div>
  );
}

function ReportPanel({ report }) {
  const un = report.unassigned || {};
  const groups = un.groups || [];
  const unmatched = report.seed?.unmatched_tokens || [];
  return (
    <div data-testid="perf-centers-report" style={{ marginTop: '12px', fontSize: '12px', color: COLORS.text }}>
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>보고</div>
      <div data-testid="perf-centers-unassigned">
        미배정(실적 센터를 못 정한 1단계 저장값): 업로드 {un.uploads || 0}건 · 출고 {un.shipments || 0}행 · 근무 {un.labor || 0}행
      </div>
      {groups.length > 0 && (
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
          {groups.map((g) => (
            <li key={`${g.file_center_raw}|${g.center_token}`}>
              B2 «{g.file_center_raw || '—'}» (권한 토큰 {g.center_token || '—'}) — 업로드 {g.uploads}건 · 출고 {g.shipments}행 · 근무 {g.labor}행
              · 이 문구를 실적 센터 별칭으로 더하면 다시 이어집니다
            </li>
          ))}
        </ul>
      )}
      {unmatched.length > 0 && (
        <div style={{ marginTop: '6px', color: COLORS.warn }}>
          조직도에 없어 권한 센터를 비운 실적 센터: {unmatched.map((u) => `${u.name}(희망 ${u.wanted})`).join(', ')}
        </div>
      )}
    </div>
  );
}
